import { useEffect, useState } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LiveBanner } from "@/components/live/LiveBanner";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { routes } from "@/routes";

// Plan 03-06: ThemeProvider is outermost wrapper (D-21); storageKey="theme" (D-25)
// Plan 04-02: Routes block is now data-driven via src/routes.tsx (consumed by both
//             dev mode here and SSG mode via src/ssg.tsx).
//
// REMOVIDOS em 2026-09-05 (auditoria de peso da home). Os tres estavam no bundle
// critico sem renderizar nada, somando ~79 KB nao comprimidos:
//
//   QueryClientProvider (@tanstack/react-query, ~27 KB) — nao ha um unico useQuery,
//     useMutation ou useQueryClient no projeto. Os dados vem de JSON bundlado.
//   TooltipProvider (@radix-ui/react-tooltip + popper + floating-ui, ~32 KB) — nao ha
//     um unico <TooltipTrigger>/<TooltipContent> no projeto.
//   <Toaster /> (@radix-ui/react-toast, ~20 KB) — o hook use-toast que o alimenta nao
//     e importado por ninguem; todo toast real do site usa o Sonner, que fica.
//
// <Sonner /> FICA: e usado de verdade em Redirector e em VoteButtons.
//
// REMOVIDOS DE VEZ em 2026-10-20, pedido do operador: o convite de push
// (PushSlidePrompt), o aviso de saída (ExitIntentModal) e a roleta que ele
// abria (SpinToWin). Na prática nenhum abria para visitante novo: os dois
// exigiam o cookie de consentimento, que só o ConsentBanner (removido em
// 2026-08-08) criava. O service worker segue tratando push para quem já tinha
// assinado. Sobra o InstallPrompt (PWA) — que tem o MESMO bloqueio de
// consentimento, ver useInstallPrompt.ts.
// Landings de nicho que NÃO recebem os overlays da home. Em /camping o exit
// intent mandava o visitante pro grupo PRINCIPAL (e mostrava Telegram), o push e
// o PWA falam do site principal e o live banner é da live da home. Numa landing
// de um botão só, qualquer um desses rouba o clique do único CTA que importa.
const ROTAS_ISOLADAS = ["/camping"];

function isRotaIsolada(pathname: string): boolean {
  return ROTAS_ISOLADAS.some((r) => pathname === r || pathname.startsWith(`${r}/`));
}

// Precisa estar DENTRO do BrowserRouter para ler a rota.
function OverlaysGlobais() {
  const { pathname } = useLocation();
  if (isRotaIsolada(pathname)) return null;
  return (
    <>
      <LiveBanner />
      {/* ConsentBanner removido 2026-08-08 (pedido do operador): atrapalhava o clique
          no CTA do WhatsApp. Site passa a usar SÓ cookies essenciais — Clarity/Fomo/
          PostHog são consent-gated e sem banner nunca recebem consent, logo não carregam.
          Pixel/GA4 seguem por legítimo interesse (fora do gate). Ver src/lib/consent.ts. */}
      <InstallPrompt />
    </>
  );
}

// Toasts e overlays. Todos são position:fixed: a posição deles no DOM não muda
// nada na tela. O ThemeProvider só envolve o Sonner, o único que lê o tema
// (useTheme).
function Extras() {
  return (
    <>
      <ThemeProvider>
        <Sonner />
      </ThemeProvider>
      <OverlaysGlobais />
    </>
  );
}

function DepoisDeMontar() {
  const [montado, setMontado] = useState(false);
  useEffect(() => setMontado(true), []);
  return montado ? <Extras /> : null;
}

/**
 * HIDRATAÇÃO (2026-10-23): quando o src/main.tsx aproveita o HTML do SSG, a
 * PRIMEIRA renderização daqui precisa dar exatamente o DOM que o SSG gerou — e
 * o SSG (src/ssg.tsx) renderiza só a página. Então a página vem primeiro e os
 * Extras entram depois de montar, no fim do #root.
 *
 * Sem hidratação (/r/<slug>, 404) os Extras ficam ANTES das rotas, como sempre
 * foi: o Sonner só mostra toast criado depois de ele montar, e o efeito dele
 * precisa rodar antes do efeito da página — o Redirector chama toast.error()
 * logo ao montar.
 */
const App = ({ hidratando = false }: { hidratando?: boolean }) => (
  <BrowserRouter>
    {!hidratando && <Extras />}
    <Routes>
      {routes.map((r) => (
        <Route key={r.path} path={r.path} element={r.Component ? <r.Component /> : null} />
      ))}
    </Routes>
    {hidratando && <DepoisDeMontar />}
  </BrowserRouter>
);

export default App;
